import {Component, inject, Input, OnInit} from '@angular/core';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MemoryService} from '../../../../core/services/memory.service';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {BookingService} from '../../booking.service';
import {BookAppointmentsDto} from '../../dtos/book-appointments.dto';
import {AppointmentSelectionService} from '../../../appointment-selection/appointment-selection.service';
import {AppointmentResponseDto} from '../../dtos/appointment-response.dto';
import {AppointmentResponseCode} from '../../enums/appointment-response-code.enum';
import {ErrorService} from '../../../../core/services/error.service';
import {PatientManagementService} from '../../../patient-management/patient-management.service';
import {WindowReferenceService} from '../../../../core/services/window.service';
import {LocalizedTextDto} from '../../../../shared/dtos/localized-text.dto';
import {DateTime} from 'luxon';
import {TimeSlotDto} from '../../../appointment-selection/dtos/time-slot.dto';
import {PatientDto} from '../../../patient-management/dtos/patient.dto';
import {MatIconModule} from '@angular/material/icon';
import {StorageService} from '../../../../core/services/storage.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LocalizedTextPipe} from '../../../../shared/pipes/localized-text.pipe';
import {AppointmentDto} from '../../../../shared/dtos/appointment.dto';
import {AppointmentQueryDto} from '../../../patient-management/dtos/appointment-query.dto';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-confirm',
  imports: [
    TranslocoPipe,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TextButton,
    MatIconModule
  ],
  providers: [LocalizedTextPipe],
  templateUrl: './confirm.component.html',
  styleUrl: './confirm.component.scss'
})
export class ConfirmComponent implements OnInit {
  @Input() mobile = true;

  memoryService = inject(MemoryService);
  storageService = inject(StorageService);
  location = inject(Location)
  router = inject(Router);
  bookingService = inject(BookingService);
  appointmentSelectionService = inject(AppointmentSelectionService);
  translocoService = inject(TranslocoService);
  patientManagementService = inject(PatientManagementService);
  windowService = inject(WindowReferenceService);
  errorService = inject(ErrorService);
  bookingMessageForm: FormControl = new FormControl("");
  error?: string;
  bookingFor: boolean = false;
  patient?: PatientDto;
  localizedTextPipe: LocalizedTextPipe = inject(LocalizedTextPipe);
  latestAppointment?: AppointmentDto;

  constructor(private snackBar: MatSnackBar) {
  }

  get showBookingHint() {
    if (this.memoryService.locationDetailDto?.appearanceSettings?.hideBookingWarning == true) {
      return false;
    }

    const categoryId = this.appointmentSelectionService.timeSlotDto?.categoryId;
    const categorySummaryDto = this.appointmentSelectionService.categorySummaryDtos?.find(x => x.categoryId == categoryId);
    const categoryBookingHint = this.localizedTextPipe.transform(categorySummaryDto?.bookingHint);
    if (categoryBookingHint) {
      return true;
    }

    var result = this.localizedTextPipe.transform(this.memoryService.locationDetailDto?.appearanceSettings?.bookingHint);
    return !!result;
  }

  get bookingHint() {
    const categoryId = this.appointmentSelectionService.timeSlotDto?.categoryId;
    const categorySummaryDto = this.appointmentSelectionService.categorySummaryDtos?.find(x => x.categoryId == categoryId);
    const categoryBookingHint = this.localizedTextPipe.transform(categorySummaryDto?.bookingHint);
    if (categoryBookingHint) {
      return categoryBookingHint;
    }

    return this.localizedTextPipe.transform(this.memoryService.locationDetailDto?.appearanceSettings?.bookingHint);
  }

  get employeeName() {
    const employeeId = this.appointmentSelectionService.timeSlotDto?.employeeId;
    const employee = this.appointmentSelectionService.employeeSummaryDtos?.find(e => e.employeeId == employeeId);
    return `${employee?.firstName} ${employee?.lastName}`;
  }

  ngOnInit() {
    console.log(this.memoryService.locationDetailDto?.appearanceSettings?.hideBookingWarning);
    this.bookingFor = this.memoryService.isPatientManager ?? false;
    if (this.bookingFor) {
      this.patient = this.storageService.loadPatient();
      this.bookingFor = this.patient != null;
    }

    const appointmentQueryDto: AppointmentQueryDto = {
      patientId: this.memoryService.patientId!,
      customerId: this.memoryService.customerId,
      appointmentTypes: [1, 2, 3, 4], // everything except 5 (group appointment)
      locationIds: [this.memoryService.locationId!],
      isDeleted: false,
      dateRangeEnd: this.appointmentSelectionService.timeSlotDto?.startTime,
      maxResultCount: 1
    }

    this.patientManagementService.queryAppointments(appointmentQueryDto).subscribe({
      next: (appointmentDtos: AppointmentDto[]) => {
        if (appointmentDtos.length > 0) {
          this.latestAppointment = appointmentDtos[0];
        }
      }
    })
  }

  confirm() {
    const timeSlotDto = this.appointmentSelectionService.timeSlotDto;

    const bookAppointmentDto: BookAppointmentsDto = {
      patientId: this.memoryService.patientId!,
      locationId: this.memoryService.locationId!,
      appointmentRequests: [
        {
          categoryId: timeSlotDto?.categoryId!,
          employeeId: timeSlotDto?.employeeId!,
          bookingMessage: this.bookingMessageForm.value,
          startTime: timeSlotDto?.startTime!,
        }
      ]
    }

    if (this.bookingFor && this.patient) {
      bookAppointmentDto.patientId = this.patient.patientId;
    }

    this.pushToTagManager(timeSlotDto);
    this.bookingService.bookAppointments(bookAppointmentDto).subscribe({
      next: (appointmentResponseDtos: AppointmentResponseDto[]) => {
        if (appointmentResponseDtos.length != 1) {
          this.error = this.translocoService.translate("error.unexpected");
          return;
        }

        const appointmentResponseDto: AppointmentResponseDto = appointmentResponseDtos[0];
        switch (appointmentResponseDto.appointmentResponseCode) {
          case AppointmentResponseCode.Ok:
            this.error = undefined;
            this.patientManagementService.appointmentDtos = undefined;
            if (this.bookingFor) {
              this.snackBar.open(this.translocoService.translate('booking.success.title'), 'OK', {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'bottom'
              });
              this.router.navigate(['/appointment-selection']);
              break;
            }

            this.router.navigate(['/success']);
            break;
          case AppointmentResponseCode.MinFutureBookingHoursNotMet:
            this.error = this.translocoService.translate("booking.error.minFutureBookingHoursNotMet");
            break;
          case AppointmentResponseCode.MaxAppointmentsPerWeekExceeded:
            this.error = this.translocoService.translate("booking.error.maxAppointmentsPerWeekExceeded");
            break;
          case AppointmentResponseCode.MaxAppointmentsExceeded:
            this.error = this.translocoService.translate("booking.error.maxAppointmentsExceeded");
            break;
          case AppointmentResponseCode.MaxAppointmentFrequencyExceeded:
            this.error = this.translocoService.translate("booking.error.maxAppointmentFrequencyExceeded");
            break;
          case AppointmentResponseCode.MaxFutureBookingHoursExceeded:
            this.error = this.translocoService.translate("booking.error.maxFutureBookingHoursExceeded");
            break;
          case AppointmentResponseCode.EmployeeBookingConstraintsNotMet:
          case AppointmentResponseCode.CategoryBookingConstraintsNotMet:
          case AppointmentResponseCode.TimeSlotNotAvailable:
            this.error = this.translocoService.translate("booking.error.timeSlotNotAvailable");
            break;
          case AppointmentResponseCode.DuplicatePatientRecords:
            this.error = this.translocoService.translate("booking.error.duplicatePatientRecords");
            break;
          default:
            this.error = this.translocoService.translate("error.unexpected");
            break;
        }
      },
      error: (error : HttpErrorResponse) => {
        this.error = undefined;

        if (error.status == 400 && error.error?.errorCode === "missingAddressInformation") {
          this.error = this.translocoService.translate("booking.error.missingAddressInformation");
          return;
        }

        this.errorService.handleError(error, this.translocoService.translate("error.title.booking"));
      }
    })
  }

  cancel() {
    this.location.back();
  }

  translate(value: LocalizedTextDto[] | undefined): unknown {
    if (!value) {
      return undefined;
    }

    const language = this.translocoService.getActiveLang();
    let localizedText = value.find(text => text.language === language);
    if (!localizedText && value.length > 0) {
      localizedText = value[0];
    }

    return localizedText?.value;
  }

  private pushToTagManager(timeSlotDto: TimeSlotDto | undefined) {
    if (this.windowService.pushEvents) {
      var state = 'Existing';
      if (this.memoryService.isNewUser) {
        state = 'New';
        // Reset is new state in order to prevent multiple bookings within the same session to be indicated as "new customer booking".
        this.memoryService.isNewUser = false;
      }

      const categoryId = this.appointmentSelectionService.timeSlotDto?.categoryId;
      let treatment = this.appointmentSelectionService.categorySummaryDtos?.find(c => c.categoryId == categoryId)?.name;
      const employeeId = this.appointmentSelectionService.timeSlotDto?.employeeId;
      let therapist = this.appointmentSelectionService.employeeSummaryDtos?.find(e => e.employeeId == employeeId);
      var payload = {
        event: 'appointment_completed',
        treatment: this.translate(treatment),
        location: this.memoryService.locationId ?? 0,
        therapist: `${therapist?.firstName} ${therapist?.lastName}`,
        selectedDate: DateTime.fromISO(timeSlotDto?.startTime!).setZone('Europe/Zurich').toFormat('yyyy-MM-dd'),
        time: `${DateTime.fromISO(timeSlotDto?.startTime!).setZone('Europe/Zurich').toFormat('HH:mm')} - ${DateTime.fromISO(timeSlotDto?.endTime!).setZone('Europe/Zurich').toFormat('HH:mm')}`,
        customerType: state,
        email: this.memoryService.patientEmail
      };
      this.windowService.push(payload);
    }
  }
}
